/**
 * BUSINESS LAYER (BL): SECUENCIA (SIMON SAYS MULTIPLAYER)
 * Capa de lógica de negocio y abstracción para los componentes visuales de Secuencia (Admin, Pantalla Gigante y Aficionado Móvil).
 * Provee validación de entradas, normalización de respuestas estructuradas { success, data, message },
 * integración con la proyección de pantalla gigante (ScreenV2) y auto-corrección de parámetros fuera de rango.
 * 
 * @module utilities/BL/Sequence
 */

import {
    StartSequenceMatch as DLStartMatch,
    RegisterSequenceTap as DLTap,
    FinishSequencePlayback as DLFinishPlayback,
    ResetSequenceMatch as DLResetMatch,
    GetSequenceState as DLGetState,
    ShowSequenceFrontPage as DLShowFrontPage,
    ProjectSequenceGame as DLProjectGame,
    GetSequenceHistory as DLGetHistory,
    ClearSequenceHistory as DLClearHistory,
    GetSequenceThemes as DLGetThemes,
    GetSequenceThemeById as DLGetThemeById,
    SaveSequenceTheme as DLSaveTheme,
    DeleteSequenceTheme as DLDeleteTheme,
    GetSequenceConfig as DLGetConfig,
    SaveSequenceConfig as DLSaveConfig,
    UploadSequenceMedia as DLUploadMedia
} from '../DL/Sequence';

import { SetScreenV2MainDynamic } from './ScreenV2';

/**
 * Inicia una nueva partida de Secuencia tras validar y normalizar los parámetros de juego.
 * 
 * @param {Object} configData - Configuración de la partida.
 * @param {number|string} configData.screenId - Identificador de la pantalla (obligatorio).
 * @param {number} [configData.sequenceLength=7] - Longitud de la secuencia (3 a 12).
 * @param {number} [configData.playbackSpeedMs=500] - Velocidad de destello en milisegundos (200 a 2000).
 * @param {Array<Object>} [configData.buttons=null] - Colección de botones/pads personalizados.
 * @param {string|number|null} [roomId=null] - Identificador opcional de la sala para aislamiento multi-tenant.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la partida iniciada.
 */
export const StartSequence = async (configData, roomId = null) => {
    try {
        if (!configData || configData.screenId === undefined || configData.screenId === null) {
            return {
                success: false,
                message: 'El identificador de pantalla (screenId) es obligatorio para iniciar Secuencia.'
            };
        }

        // Normalización y acotamiento de límites seguros
        const normalizedConfig = {
            ...configData,
            screenId: Number(configData.screenId) || 1,
            sequenceLength: Math.min(Math.max(Number(configData.sequenceLength) || 7, 3), 12),
            playbackSpeedMs: Math.min(Math.max(Number(configData.playbackSpeedMs) || 500, 200), 2000)
        };

        return await DLStartMatch(normalizedConfig, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error inesperado en la lógica de negocio al iniciar la partida de Secuencia.'
        };
    }
};

/** Alias de StartSequence */
export const StartSequenceMatch = StartSequence;
export const StartSequenceGame = StartSequence;

/**
 * Registra un toque del jugador en un pad específico.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string} matchId - ID único de la partida en curso.
 * @param {number} colorId - Índice del pad presionado (0 a 5).
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado de la partida.
 */
export const RegisterTap = async (screenId, matchId, colorId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }
        if (!matchId) {
            return { success: false, message: 'El ID de la partida activa es requerido para registrar el toque.' };
        }
        if (colorId === undefined || colorId === null || isNaN(Number(colorId))) {
            return { success: false, message: 'El ID del botón/color presionado es requerido.' };
        }

        return await DLTap(screenId, matchId, Number(colorId), roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al registrar el toque en Secuencia.'
        };
    }
};

/** Alias de RegisterTap */
export const RegisterSequenceTap = RegisterTap;

/**
 * Notifica que la pantalla terminó la animación de destello de la secuencia.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string} matchId - ID de la partida.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de cambio a PLAYER_INPUT.
 */
export const FinishPlayback = async (screenId, matchId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }
        if (!matchId) {
            return { success: false, message: 'El ID de partida es requerido.' };
        }

        return await DLFinishPlayback(screenId, matchId, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al notificar la finalización de la reproducción.'
        };
    }
};

/** Alias de FinishPlayback */
export const FinishSequencePlayback = FinishPlayback;

/**
 * Reinicia la partida en curso y devuelve el estado a WAITING.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetSequence = async (screenId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido para reiniciar.' };
        }

        return await DLResetMatch(screenId, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al reiniciar la partida de Secuencia.'
        };
    }
};

/** Alias de ResetSequence */
export const ResetSequenceMatch = ResetSequence;
export const ResetSequenceGame = ResetSequence;

/**
 * Obtiene el estado en memoria de la partida de Secuencia.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actual del juego.
 */
export const GetState = async (screenId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }

        const res = await DLGetState(screenId, roomId);
        if (!res.success) {
            return {
                success: false,
                data: null,
                message: res.message || 'No fue posible obtener el estado de la partida.'
            };
        }

        return {
            success: true,
            data: res.data || null,
            message: res.message
        };
    } catch (error) {
        return {
            success: false,
            data: null,
            message: 'Error al consultar el estado de Secuencia.'
        };
    }
};

/** Alias de GetState */
export const GetSequenceState = GetState;

/**
 * Muestra la portada de Secuencia en la pantalla gigante y sincroniza ScreenV2 si está habilitado.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la proyección.
 */
export const ShowFrontPage = async (screenId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }

        const res = await DLShowFrontPage(screenId, roomId);

        // Intento seguro de señalización hacia ScreenV2
        try {
            await SetScreenV2MainDynamic(Number(screenId), 'FrontPageSequence');
        } catch (screenV2Error) {
            // Silencioso si ScreenV2 no está activo o la ruta no responde
        }

        return res;
    } catch (error) {
        return {
            success: false,
            message: 'Error al proyectar la portada de Secuencia.'
        };
    }
};

/** Alias de ShowFrontPage */
export const ShowSequenceFrontPage = ShowFrontPage;

/**
 * Proyecta el tablero de juego de Secuencia en la pantalla gigante y notifica a ScreenV2.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la proyección.
 */
export const ProjectGame = async (screenId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }

        const res = await DLProjectGame(screenId, roomId);

        // Intento seguro de señalización hacia ScreenV2
        try {
            await SetScreenV2MainDynamic(Number(screenId), 'SequenceScreen');
        } catch (screenV2Error) {
            // Silencioso si ScreenV2 no está activo
        }

        return res;
    } catch (error) {
        return {
            success: false,
            message: 'Error al proyectar el tablero de juego de Secuencia.'
        };
    }
};

/** Alias de ProjectGame */
export const ProjectSequenceGame = ProjectGame;

/**
 * Obtiene el historial de partidas disputadas en Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - ID opcional de la sala para filtrar.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de partidas históricas.
 */
export const GetSequenceHistory = async (roomId = null) => {
    try {
        const res = await DLGetHistory(roomId);
        if (!res.success) {
            return {
                success: false,
                data: [],
                message: res.message || 'No fue posible cargar el historial de partidas.'
            };
        }

        const historyArray = Array.isArray(res.data) ? res.data : (res.data?.history || []);
        return {
            success: true,
            data: historyArray,
            message: res.message
        };
    } catch (error) {
        return {
            success: false,
            data: [],
            message: 'Error al obtener el historial de Secuencia.'
        };
    }
};

/**
 * Elimina todo el historial acumulado de partidas de Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, message?: string}>} Resultado de la limpieza.
 */
export const ClearSequenceHistory = async (roomId = null) => {
    try {
        return await DLClearHistory(roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al limpiar el historial de partidas de Secuencia.'
        };
    }
};

/**
 * Obtiene el catálogo de temas visuales de Secuencia.
 * 
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @param {string|number|null} [operatorId=null] - ID opcional del operador.
 * @returns {Promise<{success: boolean, data: Array<Object>, message?: string}>} Colección de temas disponibles.
 */
export const GetSequenceThemes = async (roomId = null, operatorId = null) => {
    try {
        const res = await DLGetThemes(roomId, operatorId);
        if (!res.success) {
            return {
                success: false,
                data: [],
                message: res.message || 'No fue posible obtener el listado de temas.'
            };
        }

        const themesArray = Array.isArray(res.data) ? res.data : (res.data?.themes || []);
        return {
            success: true,
            data: themesArray,
            message: res.message
        };
    } catch (error) {
        return {
            success: false,
            data: [],
            message: 'Error al consultar los temas de Secuencia.'
        };
    }
};

/**
 * Obtiene el detalle de un tema visual por su ID.
 * 
 * @param {number|string} id - ID del tema.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema.
 */
export const GetSequenceTheme = async (id, roomId = null) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema es obligatorio.' };
        }
        return await DLGetThemeById(id, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al obtener el tema de Secuencia.'
        };
    }
};

/** Alias de GetSequenceTheme */
export const GetSequenceThemeById = GetSequenceTheme;

/**
 * Guarda o actualiza un tema visual de Secuencia, validando que contenga nombre y estructura de botones.
 * 
 * @param {Object} theme - Datos del tema.
 * @param {number} [theme.id=0] - ID del tema (0 para nuevo).
 * @param {string} theme.name - Nombre del tema.
 * @param {string|Array<Object>} theme.buttonsJson - Configuración de botones.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema guardado.
 */
export const SaveSequenceTheme = async (theme, roomId = null) => {
    try {
        if (!theme || !theme.name || !theme.name.trim()) {
            return {
                success: false,
                message: 'El nombre del tema es obligatorio.'
            };
        }

        const payload = {
            ...theme,
            name: theme.name.trim(),
            buttonsJson: typeof theme.buttonsJson === 'string'
                ? theme.buttonsJson
                : JSON.stringify(theme.buttonsJson || [])
        };

        return await DLSaveTheme(payload, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al guardar el tema de Secuencia.'
        };
    }
};

/**
 * Elimina un tema visual personalizado de Secuencia.
 * 
 * @param {number|string} id - ID del tema a eliminar.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteSequenceTheme = async (id, roomId = null) => {
    try {
        if (!id) {
            return { success: false, message: 'El ID del tema a eliminar es obligatorio.' };
        }
        return await DLDeleteTheme(id, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al eliminar el tema de Secuencia.'
        };
    }
};

/**
 * Obtiene la configuración de parámetros de Secuencia para una pantalla.
 * 
 * @param {number|string} screenId - ID de la pantalla.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetSequenceConfig = async (screenId, roomId = null) => {
    try {
        if (screenId === undefined || screenId === null) {
            return { success: false, message: 'El ID de pantalla es requerido.' };
        }

        return await DLGetConfig(screenId, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al consultar la configuración de pantalla de Secuencia.'
        };
    }
};

/**
 * Guarda y normaliza los parámetros de configuración de Secuencia para una pantalla.
 * 
 * @param {Object} config - Configuración.
 * @param {number|string} config.screenId - ID de pantalla.
 * @param {number|null} [config.themeId=null] - ID de tema activo.
 * @param {number} [config.sequenceLength=7] - Pasos (3 a 12).
 * @param {number} [config.playbackSpeedMs=500] - Velocidad en ms (200 a 2000).
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de guardado.
 */
export const SaveSequenceConfig = async (config, roomId = null) => {
    try {
        if (!config || config.screenId === undefined || config.screenId === null) {
            return { success: false, message: 'El ID de pantalla es obligatorio para guardar la configuración.' };
        }

        const normalizedConfig = {
            ...config,
            screenId: Number(config.screenId) || 1,
            themeId: config.themeId ? Number(config.themeId) : null,
            sequenceLength: Math.min(Math.max(Number(config.sequenceLength) || 7, 3), 12),
            playbackSpeedMs: Math.min(Math.max(Number(config.playbackSpeedMs) || 500, 200), 2000)
        };

        return await DLSaveConfig(normalizedConfig, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al guardar la configuración de Secuencia.'
        };
    }
};

/**
 * Sube y valida un recurso multimedia para los botones de Secuencia.
 * 
 * @param {File|Blob|FormData} file - Archivo de imagen a subir.
 * @param {string|number|null} [roomId=null] - ID opcional de la sala.
 * @returns {Promise<{success: boolean, url?: string, data?: Object, message?: string}>} URL pública del archivo.
 */
export const UploadSequenceMedia = async (file, roomId = null) => {
    try {
        if (!file) {
            return { success: false, message: 'No se ha seleccionado ningún archivo para subir.' };
        }

        return await DLUploadMedia(file, roomId);
    } catch (error) {
        return {
            success: false,
            message: 'Error al procesar la subida del recurso multimedia.'
        };
    }
};

export default {
    StartSequence,
    StartSequenceMatch,
    StartSequenceGame,
    RegisterTap,
    RegisterSequenceTap,
    FinishPlayback,
    FinishSequencePlayback,
    ResetSequence,
    ResetSequenceMatch,
    ResetSequenceGame,
    GetState,
    GetSequenceState,
    ShowFrontPage,
    ShowSequenceFrontPage,
    ProjectGame,
    ProjectSequenceGame,
    GetSequenceHistory,
    ClearSequenceHistory,
    GetSequenceThemes,
    GetSequenceTheme,
    GetSequenceThemeById,
    SaveSequenceTheme,
    DeleteSequenceTheme,
    GetSequenceConfig,
    SaveSequenceConfig,
    UploadSequenceMedia
};
